import { zodResolver } from '@hookform/resolvers/zod'
import type React from 'react'
import { useForm } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import { Container } from '@/components/ui/container'

import { AirbnbIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import {
	LOGIN_FORM_MODEL,
	LoginFormSchema
} from '@/features/auth/login-form-model'
import { useLogin } from '@/features/auth/queries/use-login'
import { useAuthStore } from '@/features/auth/store/store.ts'
import { errorHandler } from '@/helpers/utils'
import { routes } from '@/routes'
import { type TKey, useTranslation } from '@/translations/i18n'

export const Login: React.FC<{ brand?: string }> = ({ brand = 'Luminate' }) => {
	const { t } = useTranslation()
	const setAuthData = useAuthStore((state) => state.setAuthData)
	const {
		register,
		handleSubmit,
		formState: { errors, touchedFields }
	} = useForm({
		mode: 'onBlur',
		resolver: zodResolver(LoginFormSchema)
	})

	//login mutation
	const { mutate: login, isPending } = useLogin({
		onError: (e) => errorHandler(e),
		onSuccess: (data) => {
			setAuthData(data)
		}
	})

	const onSubmit = handleSubmit((data) => {
		login(data)
	})

	return (
		<Container>
			<title>{t('login:page_title_meta', { brand })}</title>
			<div className='flex h-full items-center justify-center'>
				<div className='flex w-[360px] flex-col items-stretch'>
					<div className='flex justify-center'>
						<AirbnbIcon className='fill-primary-600' size={40} />
					</div>
					<h4 className='mt-6 text-center font-bold text-display-sm text-neutral-900'>
						{t('login:title', { brand })}
					</h4>
					<p className='my-2 mb-14 text-center text-md'>
						{t('login:subtitle')}
					</p>
					<form
						className='flex flex-col items-stretch gap-4'
						onSubmit={onSubmit}
					>
						<div className='flex flex-col gap-4'>
							<InputText
								errorMessage={t(
									(errors.email?.message || '') as TKey
								)}
								label={t(LOGIN_FORM_MODEL.email.label)}
								placeholder={t(
									LOGIN_FORM_MODEL.email.placeholder
								)}
								type={'email'}
								{...register(LOGIN_FORM_MODEL.email.name)}
							/>
							<InputText
								errorMessage={t(
									(errors.password?.message || '') as TKey
								)}
								label={t(LOGIN_FORM_MODEL.password.label)}
								placeholder={t(
									LOGIN_FORM_MODEL.password.placeholder
								)}
								touched={touchedFields.password}
								type={'password'}
								visibilityToggle
								{...register(LOGIN_FORM_MODEL.password.name)}
								helpText={t(
									LOGIN_FORM_MODEL.password.helpText as TKey
								)}
							/>
						</div>

						<div className='flex flex-col items-end'>
							<ButtonLinkLegacy
								size={'sm'}
								to={routes.RESET_PASSWORD.path}
								variant={'tertiary'}
							>
								{t('login:secondary_cta')}
							</ButtonLinkLegacy>
						</div>
						<div className='flex flex-col items-stretch gap-2'>
							<ButtonBrandLegacy
								disabled={isPending}
								type={'submit'}
							>
								{t('login:primary_cta')}
								{isPending && <Spinner size={16} />}
							</ButtonBrandLegacy>
						</div>
					</form>
				</div>
			</div>
		</Container>
	)
}

Login.displayName = 'Login'
